/**
 * Eine Aktion ausführen — der Schritt, der den BoardState tatsächlich verändert.
 *
 * Ein Zug besteht aus **genau einer** Aktion (§12), und jede Aktion kommt aus der Liste, die
 * die Rule Engine selbst berechnet hat (§74). Diese Funktionen prüfen deshalb **nicht erneut
 * die Legalität** — sie führen aus, was `getLegalActions` angeboten hat. Geprüft wird nur,
 * was ein Fehler im Aufrufpfad wäre: läuft die Partie, und gehört die Aktion dem Spieler, der
 * am Zug ist.
 *
 * **Der vollständige Commit-Ablauf** (§34) sieht damit so aus — die Reihenfolge ist wichtig:
 *
 * ```ts
 * const context = ruleContextOf(state, board, layout);
 * const action = findLegalAction(context, state.activePlayer, incomingId);
 * if (action === undefined) return reject();          // §59: serverseitig abweisen
 *
 * state = executeAction(state, action);               // BoardState ändern
 * state = advanceTurn(state);                         // Zug beenden, nächster Spieler
 * state = checkScoreAtTurnStart(state);               // Punkt des neuen Spielers (Nr. 21)
 * state = applyVictory(state);                        // entschieden? dann FINISHED (§26)
 * ```
 *
 * Den Zug zu **beenden** gehört ausdrücklich nicht hierher: Preview und Commit sind Phase 5,
 * und die Zugübergabe ist eine eigene Entscheidung. Eine Funktion, die beides täte, ließe
 * sich nicht für `simulateAction` benutzen.
 *
 * **`simulateAction` (§90) fällt kostenlos heraus.** Weil jede Funktion pur ist und einen
 * neuen Zustand zurückgibt, ist „simulieren" dasselbe wie „ausführen und das Ergebnis
 * verwerfen". Es braucht keine zweite Code-Bahn, die später von der echten abweichen könnte —
 * genau der Grund für die Reinheitsvorgabe aus `CLAUDE.md` Regel 4.
 */
import { capturePiece, movePiece, requirePiece, respawnPiece } from './board-state.js';
import { createRuleContext } from './rule-context.js';
import { hexEquals } from './hex.js';
import { isGoalCell } from './goal-cell.js';
import { applyFlagCaptureReset } from './scoring.js';
import { sectorsOf, setBoardState, teamsOf } from './game-state.js';
/**
 * Baut den Regelkontext aus der Partie.
 *
 * Damit kann der BoardState der Partie und der im Kontext nicht auseinanderlaufen — ein
 * Fehler, der sonst erst auffiele, wenn die Engine Züge gegen eine veraltete Stellung prüft.
 * Brett und Layout kommen von außen herein, weil sie über eine ganze Partie konstant sind:
 * sie einmal zu bauen und nur `state` zu wechseln ist der Grund, warum die Simulationsschleife
 * billig bleibt (§90, §104).
 */
export function ruleContextOf(state, board, layout) {
    return createRuleContext({
        board,
        layout,
        config: state.ruleConfig,
        state: state.boardState,
        sectors: sectorsOf(state),
        teams: teamsOf(state),
    });
}
/**
 * Schlagen heißt auf das Feld des Gegners ziehen (§21), der Gegner landet im Respawn-Pool
 * seines Besitzers (§22).
 *
 * Die Reihenfolge ist zwingend: **erst** die geschlagene Figur entfernen, **dann** den
 * Angreifer bewegen. Andernfalls wäre das Zielfeld noch besetzt.
 *
 * Stand die geschlagene Figur als Fähnrich auf dem zentralen Zielfeld, greift der Score-Reset
 * aus §24 — und zwar nur dann. Draußen ist der Fähnrich eine normale Figur (Nr. 6). Die
 * Bedingung muss **vor** dem Schlag festgehalten werden, weil die Figur danach kein Feld mehr
 * hat. Dass der Angreifer ein Fähnrich ist, braucht keine eigene Prüfung: auf dem Zielfeld
 * darf ohnehin nur ein Fähnrich schlagen (`goal-cell.ts`).
 */
function executeCapture(state, action) {
    const victim = requirePiece(state.boardState, action.victim);
    const flagBearerOnGoalCell = victim.type === 'flagBearer' && isGoalCell(victim.at);
    let board = capturePiece(state.boardState, victim.id);
    board = movePiece(board, action.piece, action.to);
    const next = setBoardState(state, board);
    return flagBearerOnGoalCell ? applyFlagCaptureReset(next, victim.owner) : next;
}
/**
 * Der PUSH: die gestoßene Figur weicht, der Stoßer rückt höchstens nach (§18).
 *
 * Auch hier ist die Reihenfolge zwingend — **erst** das Opfer, **dann** der Stoßer. Dessen
 * Zielfeld kann genau das Feld sein, das das Opfer gerade verlassen hat; andernfalls stünde
 * dort noch eine Figur. Genau an dieser Reihenfolge war in Phase 3d schon einmal ein
 * Rechenfehler aufgefallen.
 *
 * Über den Brettrand gestoßene Figuren gelten als **geschlagen** und landen im Respawn-Pool
 * (`push.canPushOffBoard`). Bleibt der Stoßer stehen — der Default `pusherAdvance = 0` —, wird
 * er nicht bewegt: `movePiece` auf das eigene Feld wäre ein Fehler, weil das Feld besetzt ist.
 */
function executePush(state, action) {
    let board = state.boardState;
    if (action.capturedByPush) {
        board = capturePiece(board, action.victim);
    }
    else if (action.victimTo !== undefined) {
        board = movePiece(board, action.victim, action.victimTo);
    }
    if (!hexEquals(action.pusherTo, action.from)) {
        board = movePiece(board, action.piece, action.pusherTo);
    }
    return setBoardState(state, board);
}
/**
 * Führt eine legale Aktion aus und gibt den neuen Partiezustand zurück.
 *
 * Verändert wird ausschließlich der BoardState — und bei einem Fähnrich-Schlag im Zentrum die
 * Punktzahl (§24). Zugnummer, aktiver Spieler und Status bleiben unberührt; den Zug beendet
 * `advanceTurn`.
 */
export function executeAction(state, action) {
    if (state.status !== 'PLAYING') {
        throw new Error(`Eine Aktion lässt sich nur im Status PLAYING ausführen, nicht in ${state.status}`);
    }
    if (action.player !== state.activePlayer) {
        throw new Error(`'${action.player}' ist nicht am Zug — aktiv ist '${state.activePlayer ?? 'niemand'}' (§59)`);
    }
    switch (action.type) {
        case 'MOVE':
            return setBoardState(state, movePiece(state.boardState, action.piece, action.to));
        case 'CAPTURE':
            return executeCapture(state, action);
        case 'PUSH':
            return executePush(state, action);
        case 'RESPAWN':
            return setBoardState(state, respawnPiece(state.boardState, action.piece, action.to));
    }
}
//# sourceMappingURL=execute-action.js.map